<!-- Initialization Loading State -->
<div class="init-loading-state" *ngIf="isInitializing">
  <div class="init-loading-content">
    <div class="init-loading-spinner"></div>
    <span>Loading Administration...</span>
  </div>
</div>

<!-- Access Denied State -->
<div class="access-denied-state" *ngIf="!isInitializing && !hasAdminAccess">
  <div class="access-denied-content">
    <i class="fa-solid fa-lock fa-3x"></i>
    <h2>Access Restricted</h2>
    <p>Administration is only available to organization Admins and Owners.</p>
    <p>Contact your organization administrator if you need access.</p>
  </div>
</div>

<div class="admin-container" [class.nav-collapsed]="isNavCollapsed" *ngIf="!isInitializing && hasAdminAccess">
  <!-- Left Navigation Sidebar -->
  <nav class="admin-nav">
    <!-- Nav Header with Branding -->
    <div class="nav-header">
      <div class="nav-title" *ngIf="!isNavCollapsed">
        <i class="fa-solid fa-sliders"></i>
        <span>Administration</span>
      </div>
      <button class="collapse-btn" (click)="toggleNav()" [title]="isNavCollapsed ? 'Expand' : 'Collapse'">
        <i class="fa-solid" [ngClass]="isNavCollapsed ? 'fa-angles-right' : 'fa-angles-left'"></i>
      </button>
    </div>

    <!-- Organization Management Section -->
    <div class="nav-section">
      <div class="section-label" *ngIf="!isNavCollapsed">Organization</div>
      <div class="nav-items">
        <button
          *ngFor="let section of orgSections"
          class="nav-item"
          [class.active]="selectedSection.id === section.id"
          (click)="selectSection(section)"
          [title]="isNavCollapsed ? section.label : getNavStatus(section.id).tooltip"
        >
          <i class="fa-solid {{ section.icon }}"></i>
          <span class="nav-label" *ngIf="!isNavCollapsed">{{ section.label }}</span>
          <!-- Status Indicator -->
          <span class="nav-status"
                *ngIf="getNavStatus(section.id).status !== 'none'"
                [class.status-good]="getNavStatus(section.id).status === 'good'"
                [class.status-warning]="getNavStatus(section.id).status === 'warning'"
                [class.status-error]="getNavStatus(section.id).status === 'error'"
                [title]="getNavStatus(section.id).tooltip">
            <i class="fa-solid"
               [class.fa-check-circle]="getNavStatus(section.id).status === 'good'"
               [class.fa-exclamation-circle]="getNavStatus(section.id).status === 'warning'"
               [class.fa-times-circle]="getNavStatus(section.id).status === 'error'"></i>
          </span>
        </button>
      </div>
    </div>

    <!-- Advanced Section (developer-ish plumbing, collapsed by default) -->
    <div class="nav-section">
      <button class="section-label section-toggle" *ngIf="!isNavCollapsed" (click)="toggleAdvancedGroup()">
        <span>Advanced</span>
        <i class="fa-solid" [ngClass]="advancedExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
      </button>
      <div class="nav-items" *ngIf="advancedExpanded || isNavCollapsed">
        <button
          *ngFor="let section of advancedSections"
          class="nav-item"
          [class.active]="selectedSection.id === section.id"
          (click)="selectSection(section)"
          [title]="isNavCollapsed ? section.label : ''"
        >
          <i class="fa-solid {{ section.icon }}"></i>
          <span class="nav-label" *ngIf="!isNavCollapsed">{{ section.label }}</span>
        </button>
      </div>
    </div>

    <!-- Spacer to push restricted/system settings to bottom -->
    <div class="nav-spacer"></div>

    <!-- Restricted Section (IsOwner only) - at bottom -->
    <div class="nav-section restricted-section" *ngIf="isSystemDeveloper">
      <button class="section-label section-toggle" *ngIf="!isNavCollapsed" (click)="toggleRestrictedGroup()">
        <span><i class="fa-solid fa-lock"></i> Restricted</span>
        <i class="fa-solid" [ngClass]="restrictedExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
      </button>
      <div class="nav-items" *ngIf="restrictedExpanded || isNavCollapsed">
        <button
          *ngFor="let section of restrictedSections"
          class="nav-item"
          [class.active]="selectedSection.id === section.id"
          (click)="selectSection(section)"
          [title]="isNavCollapsed ? section.label : ''"
        >
          <i class="fa-solid {{ section.icon }}"></i>
          <span class="nav-label" *ngIf="!isNavCollapsed">{{ section.label }}</span>
        </button>
      </div>
    </div>

    <!-- System Settings Button (bottom) -->
    <div class="nav-footer">
      <button class="system-settings-btn" (click)="openSystemSettings()" [title]="isNavCollapsed ? 'System Settings' : ''">
        <i class="fa-solid fa-gear"></i>
        <span *ngIf="!isNavCollapsed">System Settings</span>
      </button>
    </div>
  </nav>

  <!-- Main Content Area -->
  <main class="admin-content">
    <!-- Content Header -->
    <div class="content-header">
      <div class="header-info">
        <i class="fa-solid {{ selectedSection.icon }}"></i>
        <div class="header-text">
          <h1>{{ selectedSection.label }}</h1>
          <p>{{ selectedSection.description }}</p>
        </div>
      </div>
      <!-- Org context moved to the global header switcher -->
    </div>

    <!-- Dynamic Content -->
    <div class="content-body">
      <!-- Settings -->
      <izzy-settings *ngIf="selectedSection.id === 'settings'" [organizationId]="selectedOrg?.ID" (statusChange)="onPanelStatusChange('settings', $event)"></izzy-settings>

      <!-- Members -->
      <izzy-organization-members *ngIf="selectedSection.id === 'members'" [organizationId]="selectedOrg?.ID" (statusChange)="onPanelStatusChange('members', $event)"></izzy-organization-members>

      <!-- Channels -->
      <!-- Channels + Run History share the section behind a small tab bar -->
      <div class="channels-section" *ngIf="selectedSection.id === 'channels'">
        <div class="section-tab-bar">
          <button [class.active]="channelsTab === 'channels'" (click)="channelsTab = 'channels'">
            <i class="fa-solid fa-satellite-dish"></i> Channels
          </button>
          <button [class.active]="channelsTab === 'runs'" (click)="channelsTab = 'runs'">
            <i class="fa-solid fa-history"></i> Run History
          </button>
        </div>
        <div class="channels-section-body">
          <izzy-channels *ngIf="channelsTab === 'channels'" [organizationId]="selectedOrg?.ID" (statusChange)="onPanelStatusChange('channels', $event)"></izzy-channels>
          <izzy-channel-runs *ngIf="channelsTab === 'runs'" (statusChange)="onPanelStatusChange('channel-runs', $event)"></izzy-channel-runs>
        </div>
      </div>

      <!-- Ticketing (consolidated: Teams, SLAs, Assignment Rules, Skills, Automation, Macros as sub-tabs) -->
      <izzy-ticketing-admin *ngIf="selectedSection.id === 'ticketing'" [organizationId]="selectedOrg?.ID" (statusChange)="onPanelStatusChange('ticketing', $event)"></izzy-ticketing-admin>

      <!-- Organization Actions -->
      <izzy-organization-actions *ngIf="selectedSection.id === 'org-actions'" [organizationId]="selectedOrg?.ID" (statusChange)="onPanelStatusChange('org-actions', $event)"></izzy-organization-actions>

      <!-- AI Configuration -->
      <izzy-ai-configuration *ngIf="selectedSection.id === 'ai'" [organizationId]="selectedOrg?.ID" (statusChange)="onPanelStatusChange('ai', $event)"></izzy-ai-configuration>

      <!-- Credentials -->
      <izzy-credentials *ngIf="selectedSection.id === 'credentials'" [organizationId]="selectedOrg?.ID" (statusChange)="onPanelStatusChange('credentials', $event)"></izzy-credentials>

      <!-- Setup Agent -->
      <izzy-setup-agent *ngIf="selectedSection.id === 'setup-agent'" [organizationId]="selectedOrg?.ID" (statusChange)="onPanelStatusChange('setup-agent', $event)"></izzy-setup-agent>

      <!-- API Keys (System) -->
      <izzy-api-keys *ngIf="selectedSection.id === 'api-keys'" (statusChange)="onPanelStatusChange('api-keys', $event)"></izzy-api-keys>

      <!-- Restricted panels: ng-container guard closes the deep-link hole —
           the nav entries were already hidden for non-system users, but the
           panels themselves rendered for a ?section= URL. -->
      <ng-container *ngIf="isSystemDeveloper">

      <!-- Contacts (Restricted) -->
      <izzy-contacts *ngIf="selectedSection.id === 'contacts'" (statusChange)="onPanelStatusChange('contacts', $event)"></izzy-contacts>

      <!-- Contact Roles (Restricted) -->
      <izzy-contact-roles *ngIf="selectedSection.id === 'contact-roles'" (statusChange)="onPanelStatusChange('contact-roles', $event)"></izzy-contact-roles>

      <!-- Plans (Restricted) -->
      <izzy-plans *ngIf="selectedSection.id === 'plans'" (statusChange)="onPanelStatusChange('plans', $event)"></izzy-plans>

      <!-- Channel Types (Restricted) -->
      <izzy-channel-types *ngIf="selectedSection.id === 'channel-types'" (statusChange)="onPanelStatusChange('channel-types', $event)"></izzy-channel-types>

      <!-- Credential Types (Restricted) -->
      <izzy-credential-types *ngIf="selectedSection.id === 'credential-types'" (statusChange)="onPanelStatusChange('credential-types', $event)"></izzy-credential-types>

      <!-- Scopes (Restricted) -->
      <izzy-scopes *ngIf="selectedSection.id === 'scopes'" (statusChange)="onPanelStatusChange('scopes', $event)"></izzy-scopes>

      <!-- Action Gallery (Restricted) -->
      <izzy-action-gallery *ngIf="selectedSection.id === 'action-gallery'" (statusChange)="onPanelStatusChange('action-gallery', $event)"></izzy-action-gallery>

      <!-- Organizations (Restricted) -->
      <izzy-organizations *ngIf="selectedSection.id === 'organizations'" (settingsNavigation)="onNavigateToSettings($event)" (organizationsChanged)="onOrganizationsChanged()"></izzy-organizations>

      <!-- Private Grants (Restricted) -->
      <izzy-private-action-grants *ngIf="selectedSection.id === 'private-grants'" (statusChange)="onPanelStatusChange('private-grants', $event)"></izzy-private-action-grants>

      </ng-container>
    </div>
  </main>

  <!-- System Settings Modal -->
  <div class="modal-overlay" *ngIf="isSystemSettingsOpen" (click)="closeSystemSettings()">
    <div class="modal-container system-settings-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <div class="modal-title">
          <i class="fa-solid fa-gear"></i>
          <span>System Settings</span>
        </div>
        <button class="modal-close" (click)="closeSystemSettings()">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
      <div class="modal-body">
        <!-- Error/Success Messages -->
        <div class="modal-message error" *ngIf="orgModalError">
          <i class="fa-solid fa-exclamation-circle"></i>
          {{ orgModalError }}
          <button (click)="orgModalError = ''"><i class="fa-solid fa-times"></i></button>
        </div>
        <div class="modal-message success" *ngIf="orgModalSuccess">
          <i class="fa-solid fa-check-circle"></i>
          {{ orgModalSuccess }}
        </div>

        <div class="settings-section">
          <h3><i class="fa-solid fa-building"></i> Organizations</h3>
          <p class="section-description">Manage your organizations and their hierarchy.</p>

          <!-- Create/Edit Org Form -->
          <div class="org-form" *ngIf="isEditingOrg || isCreatingOrg">
            <div class="form-header">
              <h4>{{ isCreatingOrg ? 'Add Organization' : 'Edit Organization' }}</h4>
            </div>
            <div class="form-body">
              <div class="form-group">
                <label>Name <span class="required">*</span></label>
                <input type="text" [(ngModel)]="orgForm.name" placeholder="Organization name" />
              </div>
              <div class="form-group">
                <label>Parent Organization</label>
                <select [(ngModel)]="orgForm.parentId">
                  <option [ngValue]="null">None (Root Organization)</option>
                  <option *ngFor="let org of getAvailableParentOrgs()" [ngValue]="org.ID">{{ org.Name }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>Description</label>
                <textarea [(ngModel)]="orgForm.description" rows="2" placeholder="Optional description"></textarea>
              </div>
            </div>
            <div class="form-actions">
              <button class="btn-primary" (click)="saveOrg()" [disabled]="isSavingOrg">
                <i class="fa-solid" [class.fa-check]="!isSavingOrg" [class.fa-spinner]="isSavingOrg" [class.fa-spin]="isSavingOrg"></i>
                {{ isCreatingOrg ? 'Create' : 'Save' }}
              </button>
              <button class="btn-secondary" (click)="cancelOrgEdit()">
                <i class="fa-solid fa-times"></i> Cancel
              </button>
            </div>
          </div>

          <!-- Org Tree List -->
          <div class="org-tree-list" *ngIf="!isEditingOrg && !isCreatingOrg">
            <div class="org-tree-item"
                 *ngFor="let node of flattenedOrgTree"
                 [style.padding-left.px]="16 + node.depth * 24"
                 [class.selected]="editingOrgId === node.org.ID">
              <!-- Expand/Collapse Toggle -->
              <button class="org-expand-btn"
                      *ngIf="node.hasChildren"
                      (click)="toggleOrgExpand(node, $event)">
                <i class="fa-solid"
                   [class.fa-chevron-down]="node.expanded"
                   [class.fa-chevron-right]="!node.expanded"></i>
              </button>
              <span class="org-expand-placeholder" *ngIf="!node.hasChildren"></span>

              <!-- Org Avatar -->
              <div class="org-avatar-sm" [class.parent-org]="node.hasChildren">
                {{ getOrgInitials(node.org.Name) }}
              </div>

              <!-- Org Info -->
              <div class="org-details">
                <span class="org-name">{{ node.org.Name }}</span>
                <span class="org-stats">
                  {{ getOrgChannels(node.org).length }} channels
                  <span *ngIf="node.hasChildren" class="sub-org-count">
                    <i class="fa-solid fa-sitemap"></i> {{ node.children.length }} sub-orgs
                  </span>
                </span>
              </div>

              <!-- Action Buttons -->
              <button class="org-action-btn" title="Add sub-organization" (click)="startCreateSubOrg(node.org)">
                <i class="fa-solid fa-plus"></i>
              </button>
              <button class="org-action-btn" title="Edit organization" (click)="startEditOrg(node.org)">
                <i class="fa-solid fa-pencil"></i>
              </button>
              <button class="org-action-btn delete-btn" title="Delete organization"
                      (click)="confirmDeleteOrg(node)"
                      *ngIf="!node.hasChildren && getOrgChannels(node.org).length === 0">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </div>

          <button class="add-org-btn" (click)="startCreateOrg()" *ngIf="!isEditingOrg && !isCreatingOrg">
            <i class="fa-solid fa-plus"></i>
            Add Organization
          </button>
        </div>
      </div>

      <!-- Delete Confirmation Dialog -->
      <div class="confirm-overlay" *ngIf="confirmDeleteOrgVisible" (click)="cancelDeleteOrg()">
        <div class="confirm-dialog" (click)="$event.stopPropagation()">
          <div class="confirm-header">
            <i class="fa-solid fa-exclamation-triangle"></i>
            <h3>Delete Organization</h3>
          </div>
          <div class="confirm-body">
            <p>Are you sure you want to delete <strong>{{ deleteOrgName }}</strong>?</p>
            <p class="confirm-warning">This action cannot be undone.</p>
          </div>
          <div class="confirm-actions">
            <button class="btn-danger" (click)="executeDeleteOrg()" [disabled]="isSavingOrg">
              <i class="fa-solid" [class.fa-trash]="!isSavingOrg" [class.fa-spinner]="isSavingOrg" [class.fa-spin]="isSavingOrg"></i>
              Delete
            </button>
            <button class="btn-secondary" (click)="cancelDeleteOrg()">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
